"use client";

/**
 * ImageBanner 컴포넌트
 * @description 가로로 넘기는 배너. 페이지 위치를 스스로 추적해 아래 인디케이터에 반영한다.
 *   이미지 렌더는 `renderItem` 이 맡는다 — DS 는 이미지를 그리지 않는다. 앱
 *   `shared/ui/ImageBanner` 의 웹 포팅. 넓은 화면의 앞뒤 버튼은 웹이 더한 것이다.
 * @example
 * <ImageBanner data={banners} label="Banner" keyExtractor={b => String(b.bannerId)}
 *   renderItem={b => <a href={b.linkUrl}><AppImage uri={b.imageUrl} /></a>} />
 */
import { IconButton, Indicator, Media } from "@fanwork-web/design-system";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { memo } from "react";

import { imageBannerStyles } from "./ImageBanner.styles";
import type { ImageBannerProps } from "./ImageBanner.types";
import { useImageBannerPaging } from "./useImageBannerPaging";

const COPY = { previous: "Previous", next: "Next" } as const;
/** 페이지가 둘 이상일 때만 넘길 것이 있다 */
const MIN_PAGES = 2;

function ImageBannerInner<T>({
  data,
  renderItem,
  keyExtractor,
  label,
  previousLabel = COPY.previous,
  nextLabel = COPY.next,
  autoplayMs = 0,
  testID,
}: ImageBannerProps<T>) {
  const { trackRef, activeIndex, onScroll, goTo, pause, resume } =
    useImageBannerPaging(data.length, autoplayMs);
  const hasPages = data.length >= MIN_PAGES;

  return (
    <div className={imageBannerStyles.root} data-testid={testID}>
      <div
        className={imageBannerStyles.frame}
        onMouseEnter={pause}
        onMouseLeave={resume}
        onFocus={pause}
        onBlur={resume}
      >
        <div
          ref={trackRef}
          onScroll={onScroll}
          className={imageBannerStyles.track}
          aria-label={label}
          role="region"
        >
          {data.map((item, index) => (
            <div
              key={keyExtractor(item, index)}
              className={imageBannerStyles.page}
              data-active={index === activeIndex}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} / ${data.length}`}
            >
              <Media ratio="panorama" slot={renderItem(item, index)} />
            </div>
          ))}
        </div>
        {hasPages ? (
          <>
            <div
              className={`${imageBannerStyles.control} ${imageBannerStyles.controlPrevious}`}
            >
              <IconButton
                icon={<ChevronLeft />}
                label={previousLabel}
                tone="neutralInverse"
                radius="circle"
                onClick={() => goTo(activeIndex - 1)}
              />
            </div>
            <div
              className={`${imageBannerStyles.control} ${imageBannerStyles.controlNext}`}
            >
              <IconButton
                icon={<ChevronRight />}
                label={nextLabel}
                tone="neutralInverse"
                radius="circle"
                onClick={() => goTo(activeIndex + 1)}
              />
            </div>
          </>
        ) : null}
      </div>
      <Indicator count={data.length} activeIndex={activeIndex} label={label} />
    </div>
  );
}

export const ImageBanner = memo(ImageBannerInner) as typeof ImageBannerInner;
